/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import { FormattedMessage } from 'react-intl';
import TableBody from '@material-ui/core/TableBody';
import { Box, Button, MenuItem, Typography } from '@material-ui/core';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import Menu from '@material-ui/core/Menu';
import React, { ReactElement } from 'react';
import { observer } from 'mobx-react';
import CourseTableViewStyles from '../CourseTable/CourseTableViewStyles';
import { useViewModel } from '../../../../hooks/useViewModel';
import { CourseTableRow } from '../../../../types/course/CourseTableRow';
import { MenuItemType } from '../../../../types/course/CourseMenuItem';
import { Column } from '../../../../types/course/CourseColumn';
import { CourseTableViewModel } from './CourseTableViewModel';

interface Props<T extends CourseTableRow> {
	rows: T[];
	columns: Column[];
	menuItems: MenuItemType<T>[];
	isNewAddedRow?: (rowsLength: number, index: number) => boolean;
	emptyTableMessage?: string;
	setRow?: (row: T) => void;
}

// eslint-disable-next-line @typescript-eslint/ban-types
const CourseTableView = observer(<T extends CourseTableRow>(props: Props<T>): ReactElement => {
	const classes = CourseTableViewStyles();
	const viewModel: CourseTableViewModel<T> = useViewModel(CourseTableViewModel);
	const { rows, columns, menuItems, isNewAddedRow, emptyTableMessage, setRow } = props;

	return (
		<>
			<TableContainer>
				<Table className={classes.table} aria-label='course-table'>
					<TableHead>
						<TableRow>
							<TableCell size='small' padding='none' />
							{columns.map((column: Column, index) => (
								<TableCell key={index}>
									<FormattedMessage id={column.title} />
								</TableCell>
							))}
							<TableCell align='right' />
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((row: T, index: number) => {
							return (
								<TableRow key={index} selected={viewModel.isLastNewlyAddedRow(isNewAddedRow, rows.length, index)}>
									<TableCell className={classes.highlightCell}>
										{viewModel.isLastNewlyAddedRow(isNewAddedRow, rows.length, index) ? (
											<Box className={classes.highlight}>•</Box>
										) : null}
									</TableCell>

									{columns.map((column: Column) => {
										const value: T[keyof T] = row[column.id as keyof T];
										return (
											<TableCell
												className={classes.rowContainer}
												key={column.id}
												onClick={() => {
													if (!!setRow) {
														setRow(row);
													}
												}}>
												{viewModel.returnCellValue(value, column.format)}
											</TableCell>
										);
									})}
									<TableCell align='right'>
										{menuItems.length !== 0 && (
											<Button
												className={classes.menuButton}
												aria-describedby={'course-table-menu'}
												onClick={(event: React.MouseEvent<HTMLButtonElement>) => viewModel.toggleMenu(event, row)}>
												<MoreHorizIcon fontSize={'small'} />
											</Button>
										)}
									</TableCell>
								</TableRow>
							);
						})}

						<Menu
							id='course-table-menu'
							anchorEl={viewModel.menu.anchor}
							getContentAnchorEl={null}
							keepMounted
							open={viewModel.menu.open}
							onClose={viewModel.menu.closeMenu}
							anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
							transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
							{menuItems.map(
								(
									menuItem: {
										menuText: string;
										menuFunction: (row: T | undefined) => void;
									},
									index: number
								) => (
									<MenuItem
										key={index}
										onClick={() => {
											menuItem.menuFunction(viewModel.getSelectedRow());
											viewModel.menu.closeMenu();
										}}>
										<FormattedMessage id={menuItem.menuText} />
									</MenuItem>
								)
							)}
						</Menu>
					</TableBody>
				</Table>
			</TableContainer>
			{rows.length < 1 && emptyTableMessage ? (
				<Box className={classes.emptyState} display={'flex'} justifyContent={'center'} alignItems={'center'}>
					<Typography>
						<FormattedMessage id={emptyTableMessage} />
					</Typography>
				</Box>
			) : null}
		</>
	);
});

export default CourseTableView;
